import {
  type KeyboardEvent as ReactKeyboardEvent,
  type UIEvent as ReactUIEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import type { AssetSearchProps, EnrichedAsset } from "./AssetSearch.types";
import type { AssetSearchTabId } from "./useAssetSearchQueries";

export interface AssetSearchKeyboardContext {
  assets: readonly EnrichedAsset[];
  isLoading: boolean;
}

export interface AssetSearchScrollContext {
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

export interface UseAssetSearchUiInput {
  value: AssetSearchProps["value"];
  onChange: AssetSearchProps["onChange"];
  onSelect: AssetSearchProps["onSelect"];
  onClear: AssetSearchProps["onClear"];
  onNavigate: (symbol: string) => void;
}

export interface AssetSearchUiValue {
  query: string;
  isOpen: boolean; // effective: rawIsOpen && !!query.trim()
  activeTab: AssetSearchTabId;
  highlightedIndex: number;
  containerRef: RefObject<HTMLDivElement>;
  dropdownRef: RefObject<HTMLDivElement>;
  listRef: RefObject<HTMLDivElement>;
  onQueryChange: (value: string) => void;
  onFocus: () => void;
  onClose: () => void;
  onTabChange: (id: AssetSearchTabId) => void;
  onSelectAsset: (asset: EnrichedAsset) => void;
  onKeyDown: (
    event: ReactKeyboardEvent<HTMLInputElement>,
    context: AssetSearchKeyboardContext,
  ) => void;
  onScroll: (
    event: ReactUIEvent<HTMLDivElement>,
    context: AssetSearchScrollContext,
  ) => void;
}

export const useAssetSearchUi = ({
  value,
  onChange,
  onSelect,
  onClear,
  onNavigate,
}: UseAssetSearchUiInput): AssetSearchUiValue => {
  const [internalQuery, setInternalQuery] = useState<string>("");
  const isControlled = value !== undefined;
  const query = isControlled ? value : internalQuery;

  const [rawIsOpen, setRawIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<AssetSearchTabId>("all");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const justSelectedTimestampRef = useRef<number | null>(null);

  const effectiveIsOpen = rawIsOpen && !!query.trim();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset highlight when open state or query changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [effectiveIsOpen, query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInside =
        (containerRef.current?.contains(target) ?? false) ||
        (dropdownRef.current?.contains(target) ?? false);
      if (!isInside) {
        setRawIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleQueryChange = (val: string) => {
    if (!isControlled) {
      setInternalQuery(val);
    }
    onChange?.(val);
    if (val.trim()) {
      setRawIsOpen(true);
    } else {
      setRawIsOpen(false);
    }
  };

  const handleFocus = () => {
    if (
      justSelectedTimestampRef.current !== null &&
      Date.now() - justSelectedTimestampRef.current < 500
    ) {
      return;
    }
    justSelectedTimestampRef.current = null;
    if (query.trim()) {
      setRawIsOpen(true);
    }
  };

  const handleClose = () => setRawIsOpen(false);

  // Switching tabs swaps the result list, so an index carried over from the
  // previous tab would point at a different asset — or at no option at all.
  const handleTabChange = (id: AssetSearchTabId) => {
    setActiveTab(id);
    setHighlightedIndex(-1);
  };

  const handleSelect = (asset: EnrichedAsset) => {
    setRawIsOpen(false);
    justSelectedTimestampRef.current = Date.now();
    if (onSelect) {
      onSelect(asset.symbol);
    } else {
      onNavigate(asset.symbol);
    }
  };

  const scrollToHighlighted = (index: number) => {
    if (listRef.current) {
      const items = listRef.current.querySelectorAll("[data-asset-item]");
      items[index]?.scrollIntoView?.({ block: "nearest" });
    }
  };

  const handleKeyDown = (
    e: ReactKeyboardEvent<HTMLInputElement>,
    context: AssetSearchKeyboardContext,
  ) => {
    const { assets, isLoading } = context;

    if (e.key === "Escape" && effectiveIsOpen) {
      e.preventDefault();
      handleClose();
      return;
    }
    if (e.key === "Escape" && query && onClear) {
      e.stopPropagation();
      onClear();
      return;
    }

    if (!effectiveIsOpen || isLoading || assets.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const nextDown =
          highlightedIndex < assets.length - 1
            ? highlightedIndex + 1
            : highlightedIndex;
        setHighlightedIndex(nextDown);
        scrollToHighlighted(nextDown);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const nextUp = highlightedIndex > 0 ? highlightedIndex - 1 : 0;
        setHighlightedIndex(nextUp);
        scrollToHighlighted(nextUp);
        break;
      }
      case "Enter": {
        e.preventDefault();
        const idx = highlightedIndex === -1 ? 0 : highlightedIndex;
        if (assets[idx]) {
          handleSelect(assets[idx]);
        }
        break;
      }
    }
  };

  const handleScroll = (
    e: ReactUIEvent<HTMLDivElement>,
    context: AssetSearchScrollContext,
  ) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    const { hasMore, isLoadingMore, onLoadMore } = context;
    if (
      scrollHeight - scrollTop - clientHeight < 50 &&
      hasMore &&
      !isLoadingMore
    ) {
      onLoadMore();
    }
  };

  return {
    query,
    isOpen: effectiveIsOpen,
    activeTab,
    highlightedIndex,
    containerRef,
    dropdownRef,
    listRef,
    onQueryChange: handleQueryChange,
    onFocus: handleFocus,
    onClose: handleClose,
    onTabChange: handleTabChange,
    onSelectAsset: handleSelect,
    onKeyDown: handleKeyDown,
    onScroll: handleScroll,
  };
};
